import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  useMediaQuery
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import TabsComponent from '@components/tabs/tabs.tsx';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import useLanguageStore from '@store/language-store/language.store.tsx';
import { LanguageEnum } from '@models/language.model.ts';
import { ProductCategoriesModel } from '@models/product-categories.model.ts';
import { productCategoriesErrorMessages } from '@modules/product-categories/utils/product-categories.utils.ts';
import useProductCategoriesStore from '@modules/product-categories/store/product-categories.store.ts';
import AppAutocompleteV2 from '@shared/components/app-autocomplete/app-autocomplete-v2.tsx';
import {
  convertToSelectedDepartmentUnit,
  productsErrorMessages
} from '@modules/products/utils/products.utils';
import { ScreenBadge } from '@utils/screen-badge.utils.tsx';
import { CrudActionEnum } from '@models/global.model.ts';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import useUnitsStore from '@modules/departments-and-units/pages/units/store/units.store';
import useDepartmentStore from '@modules/departments-and-units/pages/departments/store/departments.store';
import UnitsDialogContent from '@modules/departments-and-units/pages/units/components/units-dialog-content/units-dialog-content';
import { UnitsModel } from '@shared/models/departments-and-units/units.model';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';

const ProductCategoriesDialog: React.FC<{
  onSave: (data: ProductCategoriesModel) => void;
  updateProductCategoriesData: ProductCategoriesModel | null;
}> = ({ onSave, updateProductCategoriesData }) => {
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { isLoading, departmentUnit, getDepartmentUnit } =
    useProductCategoriesStore();
  const { languageList } = useLanguageStore();
  const { createUnit } = useUnitsStore();
  const { resetSelectedMedia } = useMediaLibraryStore();
  const { departmentsList, getDepartmentsList } = useDepartmentStore();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [updateUnit, setUpdateUnit] = useState<UnitsModel | null>(null);
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    setValue,
    control,
    watch
  } = useForm<any>({ mode: 'onTouched' });

  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = useState<string>(defaultLang);
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const [multiLangFields, setMultiLangFields] = useState<{
    name: Record<string, string | undefined>;
    description: Record<string, string | undefined>;
  }>({
    name: {},
    description: {}
  });

  const selectedDepartmentUnit = useMemo(() => {
    return convertToSelectedDepartmentUnit(updateProductCategoriesData);
  }, [updateProductCategoriesData]);

  const watchedDepartmentUnit = watch('departmentUnit');

  const computedSelectedList = useMemo(() => {
    if (watchedDepartmentUnit) {
      return Array.isArray(watchedDepartmentUnit)
        ? watchedDepartmentUnit
        : [watchedDepartmentUnit];
    }
    return [];
  }, [watchedDepartmentUnit]);

  useEffect(() => {
    if (updateProductCategoriesData) {
      const { name = {}, description = {} } = updateProductCategoriesData;

      setMultiLangFields({ name, description });

      reset({
        name: name[defaultLang] || '',
        description: description[defaultLang] || '',
        departmentUnit: selectedDepartmentUnit[0] || null
      });
    } else {
      reset({
        name: '',
        description: '',
        departmentUnit: null
      });
      setMultiLangFields({ name: {}, description: {} });
    }
  }, [updateProductCategoriesData, reset, defaultLang, selectedDepartmentUnit]);

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentName = watch('name');
    const currentDescription = watch('description');

    setMultiLangFields((prev) => ({
      name: { ...prev.name, [currentTab]: currentName },
      description: { ...prev.description, [currentTab]: currentDescription }
    }));

    setCurrentTab(newLang);
    setValue('name', multiLangFields.name?.[newLang] || '');
    setValue('description', multiLangFields.description?.[newLang] || '');
  };

  const handleFormSave = async (row: any) => {
    return new Promise(async (resolve) => {
      try {
        const res: any = await createUnit({
          data: row,
          handleClose: () => {
            setOpenDialog(false);
            setUpdateUnit(null);
          }
        });

        const unitData = res?.data || res;

        const newOption = {
          id: unitData?.id || unitData?._id || unitData?.id,
          title: unitData?.name || unitData?.title,
          type: unitData?.type
        };

        setValue('departmentUnit', newOption, {
          shouldValidate: true,
          shouldDirty: true
        });

        await getDepartmentUnit();

        resolve(newOption);
      } catch (error) {
        resolve(null);
      }
    });
  };

  const onSubmit = (data: any) => {
    const currentName = watch('name');
    const currentDescription = watch('description');

    const departmentUnitId = data.departmentUnit?.id;

    const fullData: any = {
      name: { ...multiLangFields.name, [currentTab]: currentName },
      description: {
        ...multiLangFields.description,
        [currentTab]: currentDescription
      },
      image: selectedMedia?.productCategoriesImage?.path || null,
      departmentUnitId: departmentUnitId
    };

    onSave(fullData);
  };

  useEffect(() => {
    getDepartmentsList();
  }, []);

  useEffect(() => {
    if (!openDialog) {
      setUpdateUnit(null);
    }
  }, [openDialog]);

  useEffect(() => {
    return () => {
      resetSelectedMedia('productCategoriesImage');
    };
  }, []);

  return (
    <div>
      <div className="w-full">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>

      <div className="mt-16 flex gap-14 max-lg-xl:mt-10">
        <div className="media-wrapper">
          <div className="flex mb-1.5">
            <span className="reuqired-symbole">*</span>
            {t('label_image')}
          </div>
          <MediaLibraryComponent
            mediaKey={'productCategoriesImage'}
            allowedTypes={[FileTypeEnum.Image]}
            path={updateProductCategoriesData?.image}
            onSelect={(media) =>
              setSelectedMedia((prev) => ({
                ...prev,
                productCategoriesImage: media
              }))
            }
          />
        </div>

        <form className="w-full flex flex-col gap-1">
          <FormControl error={!!errors.name}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_name')}
              <InfoTooltip text={t('tooltip_info_product_category_name')} />
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              error={!!errors.name}
              placeholder={t('placeholder_product_category_name')}
            />
            <FormHelperText>
              {errors?.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages: productCategoriesErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <AppAutocompleteV2
            name="departmentUnit"
            control={control}
            selectedList={computedSelectedList}
            label={
              <span className="flex items-center gap-2">
                <span className="flex items-center">
                  <span className="reuqired-symbole">*</span>
                  {t('label_department_unit')}
                  <InfoTooltip text="tooltip_info_department_unit" />
                </span>
                {watchedDepartmentUnit && (
                  <ScreenBadge type={watchedDepartmentUnit?.type} />
                )}
              </span>
            }
            placeholder={t('placeholder_product_category_unit')}
            options={departmentUnit}
            withChip={false}
            addItemConfig={{
              action: CrudActionEnum.AddWithDialog,
              callback: (data: any) => {
                setOpenDialog(true);
                setUpdateUnit({ name: data.title });
              }
            }}
            validationProps={{
              errors,
              errorMessages: productsErrorMessages,
              getErrorMessage,
              validationRules: { required: true }
            }}
            renderOptionBadge={(option) => <ScreenBadge type={option?.type} />}
          />

          <FormControl error={!!errors.description}>
            <FormLabel className="flex mt-4">
              <span className="reuqired-symbole">*</span>
              {t('label_description')}
              <InfoTooltip
                text={t('tooltip_info_product_category_description')}
              />
            </FormLabel>
            <OutlinedInput
              multiline
              rows={5}
              {...register('description', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              placeholder={t('placeholder_product_category_description')}
            />
            <FormHelperText>
              {errors?.description &&
                getErrorMessage({
                  errors,
                  fieldName: 'description',
                  errorMessages: productCategoriesErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <div className="flex justify-end pt-4 pb-1 sticky bottom-0 bg-(--white)">
            <Button
              variant="contained"
              loading={isLoading()}
              disabled={!isValid || !selectedMedia?.productCategoriesImage}
              onClick={handleSubmit(onSubmit)}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </div>

      <AppDialog
        open={openDialog}
        size={isTabletScreen ? 'sm' : 'md'}
        title={t('unit_dialog_title')}
        content={
          <UnitsDialogContent
            departments={departmentsList}
            updateUnitData={updateUnit}
            onSave={handleFormSave}
          />
        }
        onClose={() => setOpenDialog(false)}
      />
    </div>
  );
};

export default ProductCategoriesDialog;
